import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";
import { useVoiceRecorder, silentWavUrl } from "../lib/useVoiceRecorder";
import { BookingCard } from "./BookingCard";
import { streamChat } from "../lib/streamChat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
let SILENT_URL = null;

export default function Chat({ t, lang, sessionId, setSessionId }) {
  const [messages, setMessages] = useState([]); // {role, text}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [sttEnabled, setSttEnabled] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("");
  const [transcribing, setTranscribing] = useState(false);
  const [playingIdx, setPlayingIdx] = useState(null);
  const [welcomed, setWelcomed] = useState(true); // start true; flip false after we know TTS is on and user hasn't welcomed yet
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const msgsRef = useRef([]);
  const abortRef = useRef(null);
  msgsRef.current = messages;

  // Prefill from a blog post's «Spør tvillingen om dette» link (?ask=…)
  useEffect(() => {
    const url = new URL(window.location.href);
    const ask = url.searchParams.get("ask");
    if (!ask) return;
    setInput(ask.slice(0, 500));
    url.searchParams.delete("ask");
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    setTimeout(() => {
      document.getElementById("chat")?.scrollIntoView({ behavior: "smooth", block: "start" });
      inputRef.current?.focus({ preventScroll: true });
    }, 300);
  }, []);

  // Welcome message swaps with language while no real session yet
  const welcome = { role: "assistant", text: t.chat.welcome };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/features`);
        if (!cancelled) {
          setTtsEnabled(Boolean(data.tts_enabled));
          setSttEnabled(Boolean(data.stt_enabled));
          setBookingUrl(data.booking_embed_url || "");
          const heardKey = "np-welcomed-v1";
          const hasHeard =
            typeof window !== "undefined" && window.localStorage.getItem(heardKey);
          if (data.tts_enabled && !hasHeard) {
            setWelcomed(false);
          }
        }
      } catch {
        /* keep default false */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (messages.length === 0 || (messages.length === 1 && messages[0].role === "assistant")) {
      setMessages([welcome]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text, opts = {}) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const rid = `r-${Date.now()}`;
    const patch = (fn) => setMessages((m) => m.map((x) => (x.id === rid ? { ...x, ...fn(x) } : x)));
    setMessages((m) => [
      ...m,
      { role: "user", text: msg, voice: !!opts.voice },
      { id: rid, role: "assistant", text: "", streaming: true },
    ]);
    setInput("");
    setLoading(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      await streamChat(`${API}/chat/stream`, { message: msg, session_id: sessionId, lang }, (ev) => {
        if (ev.type === "meta" && !sessionId) setSessionId(ev.session_id);
        if (ev.type === "delta") patch((x) => ({ text: x.text + ev.text }));
        if (ev.type === "done") {
          patch(() => ({ text: ev.reply, citations: ev.citations || [], blogSources: ev.blog_sources || [], booking: !!ev.show_booking, streaming: false }));
          const idx = msgsRef.current.findIndex((x) => x.id === rid);
          if (opts.voice && ttsEnabled && idx !== -1) playTTS(idx, ev.reply);
        }
      }, ctrl.signal);
    } catch (e) {
      if (ctrl.signal.aborted && abortRef.current !== ctrl) return;
      console.error(e);
      toast.error(lang === "no" ? "Beklager — fikk ikke kontakt." : "Sorry — couldn't reach the twin.");
      setMessages((m) => [
        ...m.filter((x) => x.id !== rid),
        {
          role: "assistant",
          text:
            lang === "no"
              ? "Hm, linja datt. Prøv igjen om litt — eller bruk skjemaet lenger nede for å sende beskjed til mennesket."
              : "The line dropped. Try again in a sec — or use the form below to reach the human.",
        },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const abortStream = () => {
    if (abortRef.current) {
      const c = abortRef.current;
      abortRef.current = null;
      c.abort();
      setLoading(false);
    }
  };

  const reset = () => {
    abortStream();
    setSessionId(null);
    setMessages([welcome]);
    stopAudio();
    setPlayingIdx(null);
  };

  const exportChat = async () => {
    if (!sessionId) return;
    try {
      const { data } = await axios.get(`${API}/chat/export/${sessionId}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nickolass-chat-${sessionId.slice(0, 8)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error(t.chat.exportFail);
    }
  };

  const deleteChat = async () => {
    if (!sessionId) return;
    abortStream();
    try {
      await axios.delete(`${API}/chat/session/${sessionId}`);
      toast.success(t.chat.deletedToast);
      reset();
    } catch (e) {
      console.error(e);
      toast.error(lang === "no" ? "Kunne ikke slette — prøv igjen." : "Couldn't delete — try again.");
    }
  };

  const getAudio = () => {
    if (!audioRef.current) audioRef.current = new Audio();
    return audioRef.current;
  };

  const stopAudio = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
    }
  };

  const playTTS = async (idx, text) => {
    stopAudio();
    if (playingIdx === idx) {
      setPlayingIdx(null);
      return;
    }
    try {
      setPlayingIdx(idx);
      const res = await axios.post(
        `${API}/tts`,
        { text, lang },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const audio = getAudio();
      audio.onended = () => {
        setPlayingIdx(null);
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => {
        setPlayingIdx(null);
        URL.revokeObjectURL(url);
      };
      audio.src = url;
      await audio.play();
    } catch (e) {
      console.error(e);
      setPlayingIdx(null);
      toast.error(lang === "no" ? "Kunne ikke spille av stemmen." : "Couldn't play the voice.");
    }
  };

  const onAudio = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
      fd.append("audio", blob, `voice.${ext}`);
      fd.append("lang", lang);
      const { data } = await axios.post(`${API}/stt`, fd);
      if (!data.text) {
        toast.message(lang === "no" ? "Fikk ikke med meg det — prøv igjen." : "Didn't catch that — try again.");
        return;
      }
      await send(data.text, { voice: true });
    } catch (e) {
      console.error(e);
      toast.error(lang === "no" ? "Talegjenkjenning feilet." : "Speech recognition failed.");
    } finally {
      setTranscribing(false);
    }
  };

  const recorder = useVoiceRecorder({ onAudio });

  const toggleMic = async () => {
    if (recorder.recording) return recorder.stop();
    stopAudio();
    setPlayingIdx(null);
    try {
      const a = getAudio();
      a.src = SILENT_URL || (SILENT_URL = silentWavUrl());
      a.play().catch(() => {});
    } catch { /* noop */ }
    try {
      await recorder.start();
    } catch (e) {
      console.error(e);
      toast.error(lang === "no" ? "Fikk ikke tilgang til mikrofonen." : "Couldn't access the microphone.");
    }
  };

  const acceptWelcome = async () => {
    setWelcomed(true);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("np-welcomed-v1", "1");
    }
    // Trigger the welcome TTS — counts as a user gesture so audio.play() will succeed
    await playTTS(0, welcome.text);
  };

  const dismissWelcome = () => {
    setWelcomed(true);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("np-welcomed-v1", "1");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <section
      id="chat"
      data-testid="chat-section"
      className="relative border-b border-rule bg-paper"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Left column — context */}
        <aside className="lg:col-span-4">
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-ember">
            ◐ {t.chat.title}
          </div>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-[1.05]">
            {lang === "no"
              ? "Spør meg om noe."
              : "Ask me anything."}
          </h2>
          <p className="mt-4 text-sm text-mist max-w-sm leading-relaxed">
            {lang === "no"
              ? "Jeg kjører på Claude Sonnet 5.5 og er trent på Nickolass' verktøy, prosjekter og litt for mange Sprintcamp-anekdoter."
              : "I run on Claude Sonnet 5.5, trained on Nickolass's toolkit, projects, and slightly too many Sprintcamp anecdotes."}
          </p>

          <div className="mt-8 hidden lg:block">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-mist">
              {t.chat.suggestPrefix}
            </div>
            <div className="mt-3 flex flex-col gap-2">
              {t.chat.suggestions.map((s) => (
                <button
                  key={s}
                  data-testid={`chat-suggest-${s.slice(0, 12).replace(/\s+/g, "-")}`}
                  onClick={() => send(s)}
                  className="text-left text-sm border border-rule rounded-md px-3 py-2 hover:border-ink hover:bg-sand transition-colors"
                >
                  → {s}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Right column — chat panel */}
        <div className="lg:col-span-8">
          <div className="relative border border-ink rounded-[2px] bg-sand/60 backdrop-blur-sm shadow-[6px_6px_0_0_#1A1A1A]">
            {/* First-visit voice curtain — only when TTS is enabled and user hasn't accepted yet */}
            {!welcomed && ttsEnabled && (
              <div
                data-testid="voice-curtain"
                className="absolute inset-0 z-20 bg-paper/95 backdrop-blur-md flex flex-col items-center justify-center text-center p-8 animate-fade-up"
              >
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-ember mb-4">
                  ◐ {lang === "no" ? "stemme · aktivert" : "voice · enabled"}
                </div>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight max-w-md text-ink">
                  {lang === "no" ? "Vil du høre Nickolass hilse?" : "Want to hear Nickolass say hi?"}
                </h3>
                <p className="mt-4 max-w-sm text-sm text-mist leading-relaxed">
                  {lang === "no"
                    ? "Ett trykk og du møter stemmen hans. Du kan stoppe når som helst."
                    : "One tap and you meet his voice. You can stop any time."}
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <button
                    data-testid="voice-curtain-accept"
                    onClick={acceptWelcome}
                    className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-ink text-paper font-mono text-[12px] tracking-[0.18em] uppercase hover:bg-ember transition-colors"
                  >
                    <span className="text-[14px]">▶</span>
                    {lang === "no" ? "Spill av hilsen" : "Play hello"}
                  </button>
                  <button
                    data-testid="voice-curtain-dismiss"
                    onClick={dismissWelcome}
                    className="font-mono text-[11px] tracking-[0.18em] uppercase text-mist underline underline-offset-4 hover:text-ember"
                  >
                    {lang === "no" ? "ikke nå" : "not now"}
                  </button>
                </div>
              </div>
            )}
            {/* Window chrome */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-ink/80 font-mono text-[11px] tracking-[0.18em] uppercase">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-terracotta" />
                <span className="h-2.5 w-2.5 rounded-full bg-forest" />
                <span className="h-2.5 w-2.5 rounded-full bg-ink" />
                <span className="ml-3 text-mist">terminal · {lang.toUpperCase()}</span>
              </div>
              <div className="flex items-center gap-4">
                {sessionId && (
                  <>
                    <button
                      data-testid="chat-export-btn"
                      onClick={exportChat}
                      className="text-mist hover:text-ember transition-colors"
                      title={t.chat.export}
                    >
                      ⇩<span className="hidden sm:inline"> {t.chat.export}</span>
                    </button>
                    <button
                      data-testid="chat-delete-btn"
                      onClick={deleteChat}
                      className="text-mist hover:text-ember transition-colors"
                      title={t.chat.deleteMine}
                    >
                      ✕<span className="hidden sm:inline"> {t.chat.deleteMine}</span>
                    </button>
                  </>
                )}
                <button
                  data-testid="chat-reset-btn"
                  onClick={reset}
                  aria-label={t.chat.reset}
                  title={t.chat.reset}
                  className="text-mist hover:text-ember transition-colors"
                >
                  ↻<span className="hidden sm:inline"> {t.chat.reset}</span>
                </button>
              </div>
            </div>

            {/* Transcript */}
            <div
              ref={scrollRef}
              className="np-scroll h-[420px] md:h-[480px] overflow-y-auto px-5 md:px-7 py-6 font-mono text-[13.5px] leading-[1.75]"
              data-testid="chat-transcript"
            >
              {messages.map((m, i) => (
                <React.Fragment key={i}>
                  <Bubble
                    idx={i}
                    m={m}
                    t={t}
                    ttsEnabled={ttsEnabled}
                    isPlaying={playingIdx === i}
                    onPlay={() => playTTS(i, m.text)}
                  />
                  {m.booking && <BookingCard embedUrl={bookingUrl} lang={lang} />}
                </React.Fragment>
              ))}
              {loading && !messages[messages.length - 1]?.text && (
                <div className="mt-2">
                  <span className="text-ember">{t.chat.bot}</span>
                  <span className="text-mist">: {t.chat.thinking}</span>
                  <span className="ml-1 inline-block animate-blink">▍</span>
                </div>
              )}
            </div>

            {/* Input bar */}
            <div className="border-t border-ink/80 p-3 flex items-stretch gap-2 bg-paper">
              <span className="hidden sm:inline font-mono text-[12px] tracking-[0.18em] uppercase text-ember self-center pl-2">
                {t.chat.you}:
              </span>
              <textarea
                ref={inputRef}
                data-testid="chat-input"
                rows={input.length > 70 ? 3 : 1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={
                  recorder.recording
                    ? (lang === "no" ? "Lytter… trykk ■ når du er ferdig" : "Listening… tap ■ when done")
                    : transcribing
                      ? (lang === "no" ? "Skriver ned det du sa…" : "Transcribing…")
                      : t.chat.placeholder
                }
                disabled={recorder.recording || transcribing}
                className="flex-1 resize-none bg-transparent outline-none font-mono text-[13.5px] py-2 placeholder:text-mist"
              />
              {sttEnabled && recorder.supported && (
                <MicButton
                  recording={recorder.recording}
                  busy={transcribing}
                  elapsed={recorder.elapsed}
                  disabled={loading || transcribing}
                  onClick={toggleMic}
                  lang={lang}
                />
              )}
              <button
                data-testid="chat-send-btn"
                onClick={() => send()}
                disabled={loading || !input.trim()}
                className="shrink-0 px-3 sm:px-4 py-2 bg-ink text-paper font-mono text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ember disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label={t.chat.send}
              >
                <span className="hidden sm:inline">{t.chat.send} </span>↵
              </button>
            </div>
          </div>

          {/* Mobile suggestions */}
          <div className="mt-6 lg:hidden">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-mist">
              {t.chat.suggestPrefix}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {t.chat.suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-left text-sm border border-rule rounded-md px-3 py-1.5 hover:border-ink hover:bg-sand transition-colors"
                >
                  → {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MicButton({ recording, busy, elapsed, disabled, onClick, lang }) {
  const label = recording
    ? (lang === "no" ? "Stopp opptak" : "Stop recording")
    : (lang === "no" ? "Snakk med tvillingen" : "Talk to the twin");
  return (
    <button
      data-testid="chat-mic-btn"
      onClick={onClick}
      disabled={disabled && !recording}
      aria-label={label}
      title={label}
      className={`relative shrink-0 inline-flex items-center justify-center gap-1.5 h-10 rounded-full border transition-colors disabled:opacity-40 ${
        recording
          ? "px-3 border-ember bg-ember text-paper"
          : "w-10 border-ink text-ink hover:bg-ink hover:text-paper"
      }`}
    >
      {recording && <span className="absolute inset-0 rounded-full border-2 border-terracotta animate-ping opacity-40" />}
      {busy ? <Loader2 size={16} className="animate-spin" /> : recording ? <Square size={14} fill="currentColor" /> : <Mic size={17} />}
      {recording && (
        <span data-testid="chat-mic-timer" className="font-mono text-[11px] tabular-nums">
          {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}
        </span>
      )}
    </button>
  );
}

function stripForDisplay(text, hasCites) {
  let s = text || "";
  if (hasCites) s = s.replace(/\n+\s*(\*\*)?\s*(sources|kilder)\s*:?\s*(\*\*)?\s*:?[\s\S]*$/i, "");
  s = s.replace(/\s?\[B\d\]/g, "").replace(/\s?\[B?\d?$/, "");
  return s.replace(/^\s*-{3,}\s*$/gm, "").replace(/\n{3,}/g, "\n\n").trim();
}

function RichText({ text }) {
  return text.split(/(\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") && part.length > 4
      ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
      : part.startsWith("*") && part.endsWith("*") && part.length > 2
        ? <em key={i}>{part.slice(1, -1)}</em>
        : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

function Bubble({ idx, m, t, ttsEnabled, isPlaying, onPlay }) {
  const isUser = m.role === "user";
  if (m.streaming && !m.text) return null;
  const label = isUser ? t.chat.you : t.chat.bot;
  const showPlay = !isUser && ttsEnabled && !m.streaming; // every assistant message gets a play button
  const cites = !isUser && Array.isArray(m.citations) ? m.citations : [];
  return (
    <div className="mb-4 last:mb-0 group">
      <span className={isUser ? "text-ink" : "text-ember"}>{label}</span>
      {isUser && m.voice && <Mic size={11} className="inline ml-1 -mt-0.5 text-mist" aria-label="voice" />}
      <span className="text-mist">: </span>
      <span className="whitespace-pre-wrap text-ink">
        {isUser ? m.text : <RichText text={stripForDisplay(m.text, cites.length > 0 || m.streaming)} />}
        {m.streaming && <span data-testid="chat-stream-cursor" className="ml-0.5 inline-block animate-blink text-ember">▍</span>}
      </span>
      {showPlay && (
        <button
          data-testid={`chat-tts-${idx}`}
          onClick={onPlay}
          aria-label={isPlaying ? "Stop voice" : "Play voice"}
          className={`ml-2 inline-flex items-center justify-center align-middle h-6 w-6 rounded-full border transition-all ${
            isPlaying
              ? "border-ember bg-ember text-paper animate-pulse"
              : "border-rule text-mist hover:border-terracotta hover:text-ember"
          }`}
          title={isPlaying ? "Stop" : "Play"}
        >
          <span className="text-[10px] leading-none">{isPlaying ? "■" : "▶"}</span>
        </button>
      )}
      {cites.length > 0 && (
        <div data-testid={`chat-citations-${idx}`} className="mt-2 ml-0 text-[11.5px] text-mist leading-relaxed">
          <span className="font-mono uppercase tracking-[0.18em] text-[10px] text-ember">sources:</span>{" "}
          {cites.map((c, i) => {
            let host = c.url;
            try { host = new URL(c.url).hostname.replace(/^www\./, "") + new URL(c.url).pathname.replace(/\/$/, ""); } catch {}
            return (
              <a key={c.url + i}
                 href={c.url} target="_blank" rel="noreferrer"
                 className="mr-2 underline underline-offset-2 hover:text-ember break-all">
                [{i + 1}] {host}
              </a>
            );
          })}
        </div>
      )}
      {!isUser && m.blogSources?.length > 0 && <BlogSources idx={idx} items={m.blogSources} label={t.chat.fromBlog} />}
    </div>
  );
}

function BlogSources({ idx, items, label }) {
  return (
    <div data-testid={`chat-blog-sources-${idx}`} className="mt-2 text-[11.5px] text-mist leading-relaxed">
      <span className="font-mono uppercase tracking-[0.18em] text-[10px] text-ember">{label}:</span>{" "}
      {items.map((b, i) => (
        <span key={b.slug} className="mr-3 inline-block">
          <a data-testid={`chat-blog-source-link-${idx}-${i}`} href={`/blog/${b.slug}`} target="_blank" rel="noreferrer"
             className="underline underline-offset-2 text-ink hover:text-ember transition-colors">{b.title}</a>
          {b.source_url && (
            <a data-testid={`chat-blog-source-original-${idx}-${i}`} href={b.source_url} target="_blank" rel="noreferrer"
               className="ml-1 hover:text-ember transition-colors">· {b.source_name || "original"} ↗</a>
          )}
        </span>
      ))}
    </div>
  );
}
